/**
 * VendorSettings Component
 * 
 * Settings page for vendors with inspection template management.
 * Allows vendors to create custom inspection templates with:
 * - Custom checklist items
 * - Pricing for each item
 * - Different input types (select, text, number, checkbox)
 * - Asset type filtering
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Plus, Edit2, Trash2, Save, X, Check, DollarSign, List, Tag } from 'lucide-react';
import api from '../lib/api';

const INPUT_TYPES = [
  { value: 'select', label: 'Dropdown (Select)', icon: '📋' },
  { value: 'text', label: 'Text Input', icon: '📝' },
  { value: 'number', label: 'Number Input', icon: '🔢' },
  { value: 'checkbox', label: 'Checkbox', icon: '☑️' }
];

const COMMON_ASSET_TYPES = [
  'Pallets', 'Racks', 'Bins', 'Totes', 'Crates', 'Boxes',
  'Racks (Large)', 'Bins (Large)', 'Bins (Medium)', 'Bins (Small)',
  'Totes (Large)', 'Totes (Medium)', 'Totes (Small)',
  'Crates (Large)', 'Crates (Small)', 'Shipping Rack'
];

export default function VendorSettings() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Modal state
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // Template form state
  const [templateForm, setTemplateForm] = useState({
    template_name: '',
    description: '',
    asset_types: [],
    checklist_items: [],
    base_inspection_cost: 0,
    is_default: false
  });
  
  // Checklist item being edited
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    id: '',
    label: '',
    default_cost: 0,
    input_type: 'select',
    options: ['Good', 'Damaged', 'Needs Repair']
  });

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/inspection-templates');
      setTemplates(response);
    } catch (error) {
      console.error('Failed to load templates:', error);
      alert('Failed to load templates: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({
      template_name: '',
      description: '',
      asset_types: [],
      checklist_items: [],
      base_inspection_cost: 0,
      is_default: false
    });
    setShowTemplateModal(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      template_name: template.template_name,
      description: template.description || '',
      asset_types: template.asset_types || [],
      checklist_items: typeof template.checklist_items === 'string' 
        ? JSON.parse(template.checklist_items) 
        : (template.checklist_items || []),
      base_inspection_cost: template.base_inspection_cost || 0,
      is_default: template.is_default || false
    });
    setShowTemplateModal(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.template_name.trim()) {
      alert('Template name is required');
      return;
    }
    if (templateForm.checklist_items.length === 0) {
      alert('At least one checklist item is required');
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        await api.put(`/api/inspection-templates/${editingTemplate.id}`, templateForm);
        alert('Template updated successfully!');
      } else {
        await api.post('/api/inspection-templates', templateForm);
        alert('Template created successfully!');
      }
      setShowTemplateModal(false);
      loadTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('Failed to save template: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template? This cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/api/inspection-templates/${templateId}`);
      alert('Template deleted successfully!');
      loadTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert('Failed to delete template: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await api.put(`/api/inspection-templates/${template.id}`, {
        is_active: !template.is_active
      });
      loadTemplates();
    } catch (error) {
      console.error('Failed to toggle template status:', error);
      alert('Failed to update template: ' + (error.response?.data?.message || error.message));
    }
  };

  // Checklist item management
  const handleAddChecklistItem = () => {
    setEditingItem(null);
    setItemForm({
      id: `item_${Date.now()}`,
      label: '',
      default_cost: 0,
      input_type: 'select',
      options: ['Good', 'Damaged', 'Needs Repair']
    });
  };

  const handleEditChecklistItem = (item, index) => {
    setEditingItem(index);
    setItemForm({
      id: item.id,
      label: item.label,
      default_cost: item.default_cost || 0,
      input_type: item.input_type,
      options: item.options || ['Good', 'Damaged', 'Needs Repair']
    });
  };

  const handleSaveChecklistItem = () => {
    if (!itemForm.label.trim()) {
      alert('Item label is required');
      return;
    }

    const newItem = {
      id: itemForm.id,
      label: itemForm.label.trim(),
      default_cost: parseFloat(itemForm.default_cost) || 0,
      input_type: itemForm.input_type,
      options: itemForm.input_type === 'select' ? itemForm.options : undefined
    };

    if (editingItem !== null) {
      // Update existing item
      const updatedItems = [...templateForm.checklist_items];
      updatedItems[editingItem] = newItem;
      setTemplateForm(prev => ({ ...prev, checklist_items: updatedItems }));
    } else {
      // Add new item
      setTemplateForm(prev => ({
        ...prev,
        checklist_items: [...prev.checklist_items, newItem]
      }));
    }

    setEditingItem(null);
    setItemForm({
      id: '',
      label: '',
      default_cost: 0,
      input_type: 'select',
      options: ['Good', 'Damaged', 'Needs Repair']
    });
  };

  const handleDeleteChecklistItem = (index) => {
    setTemplateForm(prev => ({
      ...prev,
      checklist_items: prev.checklist_items.filter((_, i) => i !== index)
    }));
  };

  const handleAddOption = () => {
    setItemForm(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const handleUpdateOption = (index, value) => {
    const newOptions = [...itemForm.options];
    newOptions[index] = value;
    setItemForm(prev => ({ ...prev, options: newOptions }));
  };

  const handleDeleteOption = (index) => {
    setItemForm(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const calculateTotalCost = (items) => {
    return items.reduce((sum, item) => sum + (parseFloat(item.default_cost) || 0), 0);
  };

  if (loading) {
    return (
      <div className={`p-8 h-full flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-slate-50'}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-8 h-full overflow-y-auto ${isDark ? 'bg-gray-900 text-gray-100' : 'bg-slate-50 text-gray-900'}`}>
      {/* Header */}
      <header className={`mb-6 border-b pb-6 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Vendor Settings
            </h1>
            <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Manage your inspection templates and pricing
            </p>
          </div>
          <button
            onClick={handleAddNewTemplate}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-colors"
          >
            <Plus size={20} />
            New Template
          </button>
        </div>
      </header>

      {/* Account Info */}
      <div className={`mb-6 rounded-xl p-6 border ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
        <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Your Account
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className={`rounded-lg p-3 ${isDark ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
            <div className={`text-xs uppercase mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Email</div>
            <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{user?.email || 'Not available'}</div>
          </div>
          <div className={`rounded-lg p-3 ${isDark ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
            <div className={`text-xs uppercase mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Account Type</div>
            <div className="text-sm text-amber-500 capitalize font-medium">Vendor</div>
          </div>
        </div>
      </div>

      {/* Templates List */}
      <div className={`rounded-xl border ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-white border-gray-200 shadow-sm'}`}>
        <div className={`p-4 border-b ${isDark ? 'bg-gray-900/50 border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
          <h3 className={`font-semibold ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
            Inspection Templates ({templates.length})
          </h3>
        </div>

        {templates.length === 0 ? (
          <div className="p-12 text-center">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <List size={32} className={isDark ? 'text-gray-600' : 'text-gray-400'} />
            </div>
            <h4 className={`text-lg font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              No Templates Yet
            </h4>
            <p className={`text-sm mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Create your first inspection template to get started
            </p>
            <button
              onClick={handleAddNewTemplate}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium"
            >
              Create Template
            </button>
          </div>
        ) : (
          <div className={`divide-y ${isDark ? 'divide-gray-800' : 'divide-gray-100'}`}>
            {templates.map((template) => {
              const checklistItems = typeof template.checklist_items === 'string'
                ? JSON.parse(template.checklist_items)
                : template.checklist_items;
              const totalCost = calculateTotalCost(checklistItems);

              return (
                <div key={template.id} className={`p-4 ${isDark ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'} transition-colors`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {template.template_name}
                        </h4>
                        {template.is_default && (
                          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full font-medium">
                            Default
                          </span>
                        )}
                        {!template.is_active && (
                          <span className="px-2 py-0.5 bg-gray-500/20 text-gray-400 text-xs rounded-full font-medium">
                            Inactive
                          </span>
                        )}
                      </div>
                      
                      {template.description && (
                        <p className={`text-sm mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {template.description}
                        </p>
                      )}

                      <div className="flex flex-wrap gap-3 text-xs">
                        <div className="flex items-center gap-1">
                          <List size={14} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                          <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                            {checklistItems.length} items
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <DollarSign size={14} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                          <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                            Base: ${parseFloat(template.base_inspection_cost || 0).toFixed(2)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <DollarSign size={14} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                          <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                            Total: ${totalCost.toFixed(2)}
                          </span>
                        </div>
                        {template.asset_types && template.asset_types.length > 0 && (
                          <div className="flex items-center gap-1">
                            <Tag size={14} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                            <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                              {template.asset_types.length} asset types
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleToggleActive(template)}
                        className={`p-2 rounded-lg transition-colors ${
                          template.is_active
                            ? isDark ? 'text-green-400 hover:bg-green-900/20' : 'text-green-600 hover:bg-green-50'
                            : isDark ? 'text-gray-500 hover:bg-gray-800' : 'text-gray-400 hover:bg-gray-100'
                        }`}
                        title={template.is_active ? 'Active' : 'Inactive'}
                      >
                        <Check size={18} />
                      </button>
                      <button
                        onClick={() => handleEditTemplate(template)}
                        className={`p-2 rounded-lg transition-colors ${
                          isDark ? 'text-blue-400 hover:bg-blue-900/20' : 'text-blue-600 hover:bg-blue-50'
                        }`}
                        title="Edit"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteTemplate(template.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          isDark ? 'text-red-400 hover:bg-red-900/20' : 'text-red-600 hover:bg-red-50'
                        }`}
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className={`rounded-xl shadow-2xl w-full max-w-4xl my-8 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
            {/* Modal Header */}
            <div className={`p-5 border-b flex justify-between items-center ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {editingTemplate ? 'Edit Template' : 'Create New Template'}
              </h3>
              <button
                onClick={() => setShowTemplateModal(false)}
                className={`text-2xl leading-none ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-900'}`}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Template Name */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Template Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={templateForm.template_name}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, template_name: e.target.value }))}
                  placeholder="e.g., Standard Pallet Inspection"
                  className={`w-full px-3 py-2 rounded-md border ${
                    isDark
                      ? 'bg-gray-900 border-gray-600 text-gray-100 placeholder-gray-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  } focus:outline-none focus:border-blue-500`}
                />
              </div>

              {/* Description */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Description
                </label>
                <textarea
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optional description of this template"
                  rows={2}
                  className={`w-full px-3 py-2 rounded-md border ${
                    isDark
                      ? 'bg-gray-900 border-gray-600 text-gray-100 placeholder-gray-500'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  } focus:outline-none focus:border-blue-500`}
                />
              </div>

              {/* Base Inspection Cost */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Base Inspection Cost ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={templateForm.base_inspection_cost}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, base_inspection_cost: parseFloat(e.target.value) || 0 }))}
                  className={`w-full px-3 py-2 rounded-md border ${
                    isDark
                      ? 'bg-gray-900 border-gray-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                  } focus:outline-none focus:border-blue-500`}
                />
              </div>

              {/* Asset Types (Optional) */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Asset Types (Optional - leave empty for all types)
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {COMMON_ASSET_TYPES.map((type) => {
                    const isSelected = templateForm.asset_types.includes(type);
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => {
                          if (isSelected) {
                            setTemplateForm(prev => ({
                              ...prev,
                              asset_types: prev.asset_types.filter(t => t !== type)
                            }));
                          } else {
                            setTemplateForm(prev => ({
                              ...prev,
                              asset_types: [...prev.asset_types, type]
                            }));
                          }
                        }}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          isSelected
                            ? 'bg-blue-600 text-white'
                            : isDark
                              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {type}
                      </button>
                    );
                  })}
                </div>
                {templateForm.asset_types.length > 0 && (
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Selected: {templateForm.asset_types.join(', ')}
                  </p>
                )}
              </div>

              {/* Default Template Checkbox */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_default"
                  checked={templateForm.is_default}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, is_default: e.target.checked }))}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="is_default" className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Set as default template
                </label>
              </div>

              {/* Checklist Items */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Checklist Items <span className="text-red-400">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={handleAddChecklistItem}
                    className="flex items-center gap-1 px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-md"
                  >
                    <Plus size={16} />
                    Add Item
                  </button>
                </div>

                {/* Checklist Items List */}
                {templateForm.checklist_items.length === 0 ? (
                  <div className={`p-4 rounded-lg border-2 border-dashed text-center ${
                    isDark ? 'border-gray-700 bg-gray-900/50' : 'border-gray-300 bg-gray-50'
                  }`}>
                    <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      No checklist items yet. Add items to define your inspection template.
                    </p>
                  </div>
                ) : (
                  <div className={`space-y-2 p-3 rounded-lg border ${isDark ? 'border-gray-700 bg-gray-900/30' : 'border-gray-200 bg-gray-50'}`}>
                    {templateForm.checklist_items.map((item, index) => (
                      <div
                        key={item.id}
                        className={`p-3 rounded-lg border flex items-center justify-between ${
                          isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        }`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                              {item.label}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'
                            }`}>
                              {INPUT_TYPES.find(t => t.value === item.input_type)?.icon} {item.input_type}
                            </span>
                          </div>
                          <div className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            Cost: ${parseFloat(item.default_cost || 0).toFixed(2)}
                            {item.options && item.options.length > 0 && (
                              <span className="ml-2">
                                • Options: {item.options.join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => handleEditChecklistItem(item, index)}
                            className={`p-1.5 rounded transition-colors ${
                              isDark ? 'text-blue-400 hover:bg-blue-900/20' : 'text-blue-600 hover:bg-blue-50'
                            }`}
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteChecklistItem(index)}
                            className={`p-1.5 rounded transition-colors ${
                              isDark ? 'text-red-400 hover:bg-red-900/20' : 'text-red-600 hover:bg-red-50'
                            }`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Item Form (when adding/editing) */}
                {(editingItem !== null || itemForm.label || itemForm.id) && (
                  <div className={`mt-3 p-4 rounded-lg border ${isDark ? 'border-blue-700 bg-blue-900/20' : 'border-blue-300 bg-blue-50'}`}>
                    <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-blue-300' : 'text-blue-900'}`}>
                      {editingItem !== null ? 'Edit Item' : 'New Item'}
                    </h4>
                    
                    <div className="space-y-3">
                      <div>
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          Item Label *
                        </label>
                        <input
                          type="text"
                          value={itemForm.label}
                          onChange={(e) => setItemForm(prev => ({ ...prev, label: e.target.value }))}
                          placeholder="e.g., Base Inspection"
                          className={`w-full px-3 py-2 rounded-md border text-sm ${
                            isDark
                              ? 'bg-gray-900 border-gray-600 text-gray-100'
                              : 'bg-white border-gray-300 text-gray-900'
                          } focus:outline-none focus:border-blue-500`}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            Default Cost ($)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={itemForm.default_cost}
                            onChange={(e) => setItemForm(prev => ({ ...prev, default_cost: parseFloat(e.target.value) || 0 }))}
                            className={`w-full px-3 py-2 rounded-md border text-sm ${
                              isDark
                                ? 'bg-gray-900 border-gray-600 text-gray-100'
                                : 'bg-white border-gray-300 text-gray-900'
                            } focus:outline-none focus:border-blue-500`}
                          />
                        </div>

                        <div>
                          <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            Input Type
                          </label>
                          <select
                            value={itemForm.input_type}
                            onChange={(e) => setItemForm(prev => ({ ...prev, input_type: e.target.value }))}
                            className={`w-full px-3 py-2 rounded-md border text-sm ${
                              isDark
                                ? 'bg-gray-900 border-gray-600 text-gray-100'
                                : 'bg-white border-gray-300 text-gray-900'
                            } focus:outline-none focus:border-blue-500`}
                          >
                            {INPUT_TYPES.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.icon} {type.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Options for select type */}
                      {itemForm.input_type === 'select' && (
                        <div>
                          <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            Dropdown Options
                          </label>
                          <div className="space-y-2">
                            {itemForm.options.map((option, idx) => (
                              <div key={idx} className="flex gap-2">
                                <input
                                  type="text"
                                  value={option}
                                  onChange={(e) => handleUpdateOption(idx, e.target.value)}
                                  placeholder={`Option ${idx + 1}`}
                                  className={`flex-1 px-3 py-1.5 rounded-md border text-sm ${
                                    isDark
                                      ? 'bg-gray-900 border-gray-600 text-gray-100'
                                      : 'bg-white border-gray-300 text-gray-900'
                                  } focus:outline-none focus:border-blue-500`}
                                />
                                <button
                                  type="button"
                                  onClick={() => handleDeleteOption(idx)}
                                  className={`p-1.5 rounded transition-colors ${
                                    isDark ? 'text-red-400 hover:bg-red-900/20' : 'text-red-600 hover:bg-red-50'
                                  }`}
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={handleAddOption}
                              className={`text-xs px-2 py-1 rounded ${
                                isDark ? 'text-blue-400 hover:bg-blue-900/20' : 'text-blue-600 hover:bg-blue-50'
                              }`}
                            >
                              + Add Option
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="flex gap-2 pt-2">
                        <button
                          type="button"
                          onClick={handleSaveChecklistItem}
                          className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-md font-medium"
                        >
                          <Save size={16} className="inline mr-1" />
                          Save Item
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingItem(null);
                            setItemForm({
                              id: '',
                              label: '',
                              default_cost: 0,
                              input_type: 'select',
                              options: ['Good', 'Damaged', 'Needs Repair']
                            });
                          }}
                          className={`px-3 py-2 text-sm rounded-md font-medium ${
                            isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                          }`}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Total Cost Summary */}
              {templateForm.checklist_items.length > 0 && (
                <div className={`p-4 rounded-lg border ${isDark ? 'border-green-700 bg-green-900/20' : 'border-green-300 bg-green-50'}`}>
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${isDark ? 'text-green-300' : 'text-green-900'}`}>
                      Total Template Cost:
                    </span>
                    <span className={`text-xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                      ${(parseFloat(templateForm.base_inspection_cost) + calculateTotalCost(templateForm.checklist_items)).toFixed(2)}
                    </span>
                  </div>
                  <p className={`text-xs mt-1 ${isDark ? 'text-green-400/70' : 'text-green-700/70'}`}>
                    Base: ${parseFloat(templateForm.base_inspection_cost).toFixed(2)} + Items: ${calculateTotalCost(templateForm.checklist_items).toFixed(2)}
                  </p>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className={`p-5 border-t flex justify-end gap-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => setShowTemplateModal(false)}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTemplate}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-md text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    {editingTemplate ? 'Update Template' : 'Create Template'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

